import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow, StreetViewPanorama, DirectionsRenderer } from '@react-google-maps/api';
import MapErrorBanner from '../components/MapErrorBanner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import {
  MapPin, RefreshCw, Home, Building, Car, User, AlertTriangle,
  Eye, Navigation, Layers, FolderOpen, Shield, Cross, Landmark, 
  FileText, Scale, StreetviewIcon, Map
} from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const LIBRARIES = ['places'];

// Location type colors and icons - Case Data
const LOCATION_TYPE_CONFIG = {
  HOME_ADDRESS: { color: '#10B981', icon: Home, label: 'Home', category: 'case' },
  WORK_ADDRESS: { color: '#3B82F6', icon: Building, label: 'Work', category: 'case' },
  LAST_KNOWN: { color: '#F59E0B', icon: MapPin, label: 'Last Known', category: 'case' },
  ASSOCIATE_ADDRESS: { color: '#8B5CF6', icon: User, label: 'Associate', category: 'case' },
  SURVEILLANCE_LOCATION: { color: '#EF4444', icon: Eye, label: 'Surveillance', category: 'case' },
  VEHICLE_LOCATION: { color: '#06B6D4', icon: Car, label: 'Vehicle', category: 'case' },
  OTHER: { color: '#6B7280', icon: MapPin, label: 'Other', category: 'case' }
};

// Public Safety POI types
const PUBLIC_SAFETY_TYPES = [
  { type: 'police', label: 'Police Stations', icon: Shield, color: '#3B82F6' },
  { type: 'fire_station', label: 'Fire Departments', icon: AlertTriangle, color: '#EF4444' },
  { type: 'hospital', label: 'Hospitals', icon: Cross, color: '#10B981' },
  { type: 'courthouse', label: 'Courts', icon: Scale, color: '#8B5CF6' },
  { type: 'jail', label: 'County/City Jails', icon: Landmark, color: '#F59E0B' }
];

const mapContainerStyle = {
  width: '100%',
  height: '100%'
};

const defaultCenter = {
  lat: 32.7767,
  lng: -96.7970
};

const darkMapStyle = [
  { elementType: 'geometry', stylers: [{ color: '#1d2c4d' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8ec3b9' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1a3646' }] },
  { featureType: 'water', elementType: 'geometry.fill', stylers: [{ color: '#0e1626' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#304a7d' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#255763' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] }, // Hide default POIs
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#2f3948' }] }
];

const CaseAddressMap = () => {
  const routerLocation = useLocation();
  const token = localStorage.getItem('nexus_token');
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState('all');
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [showStreetView, setShowStreetView] = useState(false);
  const [streetViewLocation, setStreetViewLocation] = useState(null);
  const [publicSafetyPlaces, setPublicSafetyPlaces] = useState([]);
  const [showPublicSafety, setShowPublicSafety] = useState(false);
  const [publicSafetyLoading, setPublicSafetyLoading] = useState(false);
  const [selectedPOI, setSelectedPOI] = useState(null);
  // Directions from ?destination_lat=&destination_lng=&directions=1
  const [directionsDestination, setDirectionsDestination] = useState(null); // { lat, lng, label, address }
  const [directionsResult, setDirectionsResult] = useState(null);
  const [directionsError, setDirectionsError] = useState('');
  const mapRef = useRef(null);
  const placesServiceRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES
  });

  const fetchCases = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setCases(await res.json());
      }
    } catch (e) {
      console.error('Fetch cases error:', e);
    }
  }, [token]);

  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${BACKEND_URL}/api/locations/map`;
      if (selectedCase && selectedCase !== 'all') {
        url += `?case_id=${selectedCase}`;
      }
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLocations(data);
        
        // Auto-center on first location
        const firstWithCoords = data.find(l => l.latitude && l.longitude);
        if (firstWithCoords) {
          setMapCenter({ lat: firstWithCoords.latitude, lng: firstWithCoords.longitude });
        }
      }
    } catch (e) {
      console.error('Fetch locations error:', e);
    } finally {
      setLoading(false);
    }
  }, [token, selectedCase]);

  // Fetch public safety places using Places API (New) - Text Search
  const fetchPublicSafetyPlaces = useCallback(async () => {
    if (!mapRef.current || !showPublicSafety) return;
    
    const bounds = mapRef.current.getBounds();
    if (!bounds) return;
    
    const center = mapRef.current.getCenter();
    const newPlaces = [];
    
    // Check if new Places API is available
    if (window.google?.maps?.places?.Place) {
      // Use new Places API with searchByText
      const { Place } = window.google.maps.places;
      
      for (const { type, label } of PUBLIC_SAFETY_TYPES) {
        try {
          // For jails, use a more specific query set (Google doesn't have a dedicated "jail" type)
          const textQueries = type === 'jail'
            ? [
                `county jail near ${center.lat()}, ${center.lng()}`,
                `city jail near ${center.lat()}, ${center.lng()}`,
                `detention center near ${center.lat()}, ${center.lng()}`,
                `correctional facility near ${center.lat()}, ${center.lng()}`
              ]
            : [`${label} near ${center.lat()}, ${center.lng()}`];

          for (const textQuery of textQueries) {
            const request = {
              textQuery,
              fields: ['displayName', 'location', 'formattedAddress', 'id'],
              locationBias: {
                center: { lat: center.lat(), lng: center.lng() },
                radius: 15000 // 15km radius — jails tend to be sparser
              },
              maxResultCount: 10
            };

            const { places } = await Place.searchByText(request);

            if (places) {
              places.forEach(place => {
                if (place.location) {
                  const name = typeof place.displayName === 'string' ? place.displayName : (place.displayName?.text || '');
                  // Avoid duplicates (same id already added)
                  if (!newPlaces.some(p => p.id === place.id)) {
                    newPlaces.push({
                      id: place.id,
                      name,
                      type: type,
                      lat: place.location.lat(),
                      lng: place.location.lng(),
                      address: place.formattedAddress
                    });
                  }
                }
              });
            }
          }
        } catch (err) {
          console.warn(`Places search failed for ${type}:`, err.message);
        }
      }
      
      setPublicSafetyPlaces(newPlaces);
    } else if (window.google?.maps?.places?.PlacesService) {
      // Fallback to legacy Places API if available
      if (!placesServiceRef.current) {
        try {
          placesServiceRef.current = new window.google.maps.places.PlacesService(mapRef.current);
        } catch (err) {
          console.warn('Could not initialize Places service:', err.message);
          toast({
            title: "Public Safety Unavailable",
            description: "Places API is not enabled for this API key",
            variant: "destructive"
          });
          return;
        }
      }
      
      const service = placesServiceRef.current;
      let completedRequests = 0;
      const totalRequests = PUBLIC_SAFETY_TYPES.length;

      PUBLIC_SAFETY_TYPES.forEach(({ type }) => {
        // Google Places legacy API doesn't have a 'jail' type; use keyword search instead
        const request = type === 'jail'
          ? { bounds: bounds, keyword: 'county jail OR city jail OR detention center' }
          : { bounds: bounds, type: type };

        try {
          service.nearbySearch(request, (results, status) => {
            completedRequests++;
            if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
              results.forEach(place => {
                if (place.geometry?.location) {
                  newPlaces.push({
                    id: place.place_id,
                    name: place.name,
                    type: type,
                    lat: place.geometry.location.lat(),
                    lng: place.geometry.location.lng(),
                    address: place.vicinity,
                    rating: place.rating
                  });
                }
              });
            }
            
            if (completedRequests === totalRequests) {
              setPublicSafetyPlaces(newPlaces);
            }
          });
        } catch (err) {
          completedRequests++;
          console.warn(`Places search failed for ${type}:`, err.message);
          if (completedRequests === totalRequests) {
            setPublicSafetyPlaces(newPlaces);
          }
        }
      });
    } else {
      console.warn('No Places API available');
      toast({
        title: "Public Safety Unavailable", 
        description: "Google Places API is not enabled",
        variant: "destructive"
      });
    }
  }, [showPublicSafety]);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  // Parse ?destination_lat=&destination_lng=&destination_address=&destination_label=&directions=1
  useEffect(() => {
    const sp = new URLSearchParams(routerLocation.search);
    const latStr = sp.get('destination_lat');
    const lngStr = sp.get('destination_lng');
    const addr = sp.get('destination_address') || '';
    const label = sp.get('destination_label') || '';
    const wantDirections = sp.get('directions') === '1';
    const lat = latStr ? parseFloat(latStr) : NaN;
    const lng = lngStr ? parseFloat(lngStr) : NaN;
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
      setDirectionsDestination({ lat, lng, label, address: addr, wantDirections });
      setMapCenter({ lat, lng });
    } else if (addr && wantDirections) {
      // No coords — fall back to address-only directions (requires the DirectionsService to geocode)
      setDirectionsDestination({ lat: null, lng: null, label, address: addr, wantDirections: true });
    }
  }, [routerLocation.search]);

  // Request directions from user's current location → destination
  useEffect(() => {
    if (!directionsDestination?.wantDirections) return;
    if (!isLoaded) return;
    if (!window.google?.maps?.DirectionsService) return;
    if (!('geolocation' in navigator)) {
      setDirectionsError('Browser geolocation is unavailable — cannot compute route.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const ds = new window.google.maps.DirectionsService();
        const destination = directionsDestination.lat != null && directionsDestination.lng != null
          ? { lat: directionsDestination.lat, lng: directionsDestination.lng }
          : directionsDestination.address;
        ds.route(
          {
            origin: { lat: pos.coords.latitude, lng: pos.coords.longitude },
            destination,
            travelMode: window.google.maps.TravelMode.DRIVING,
          },
          (result, status) => {
            if (status === 'OK' && result) {
              setDirectionsResult(result);
              setDirectionsError('');
              if (mapRef.current && result.routes?.[0]?.bounds) {
                mapRef.current.fitBounds(result.routes[0].bounds);
              }
            } else {
              setDirectionsError(`Could not compute route (${status})`);
            }
          }
        );
      },
      (err) => {
        setDirectionsError(err.code === 1 ? 'Enable location permission to see directions.' : 'Unable to get your current location.');
      },
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 30000 }
    );
  }, [directionsDestination, isLoaded]);

  const filteredLocations = locations.filter(loc => {
    if (typeFilter !== 'all' && loc.location_type !== typeFilter) return false;
    return loc.latitude && loc.longitude;
  });

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    
    // Initialize PlacesService only if available (requires Places API Legacy)
    try {
      if (window.google?.maps?.places?.PlacesService) {
        placesServiceRef.current = new window.google.maps.places.PlacesService(map);
      }
    } catch (err) {
      console.warn('Places API not available:', err.message);
    }
    
    // Fit bounds to show all markers
    if (filteredLocations.length > 1) {
      const bounds = new window.google.maps.LatLngBounds();
      filteredLocations.forEach(loc => {
        bounds.extend({ lat: loc.latitude, lng: loc.longitude });
      });
      map.fitBounds(bounds);
    }

    // Fetch public safety places after a delay (non-blocking)
    if (showPublicSafety && placesServiceRef.current) {
      setTimeout(fetchPublicSafetyPlaces, 1000);
    }
  }, [filteredLocations, fetchPublicSafetyPlaces, showPublicSafety]);

  const handleMapIdle = useCallback(() => {
    if (showPublicSafety) {
      fetchPublicSafetyPlaces();
    }
  }, [showPublicSafety, fetchPublicSafetyPlaces]);

  const centerOnLocation = (loc) => {
    if (loc.latitude && loc.longitude && mapRef.current) {
      mapRef.current.panTo({ lat: loc.latitude, lng: loc.longitude });
      mapRef.current.setZoom(16);
      setSelectedLocation(loc);
    }
  };

  const openStreetView = (lat, lng) => {
    setStreetViewLocation({ lat, lng });
    setShowStreetView(true);
  };

  const getMarkerIcon = (locationType) => {
    const config = LOCATION_TYPE_CONFIG[locationType] || LOCATION_TYPE_CONFIG.OTHER;
    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      scale: 12,
      fillColor: config.color,
      fillOpacity: 1,
      strokeColor: '#FFFFFF',
      strokeWeight: 3
    };
  };

  // Place-type specific SVG icons. Rendered as inline data: URIs so Google Maps
  // displays them directly as the marker image.
  const makePlaceSvg = (pathD, color) => {
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='40' height='48' viewBox='0 0 40 48'>
<path fill='${color}' stroke='white' stroke-width='2' d='M20 2c9.4 0 17 7.2 17 16.1 0 11.3-13.8 25.5-16 27.7-.5.5-1.5.5-2 0C16.8 43.6 3 29.4 3 18.1 3 9.2 10.6 2 20 2z'/>
<circle cx='20' cy='18' r='11' fill='white'/>
<g transform='translate(9, 7) scale(0.92)'>${pathD}</g>
</svg>`;
    return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
  };
  // Lucide-style inner glyphs (path fragments, black fill).
  const PLACE_GLYPHS = {
    police: { color: '#1D4ED8', svg: "<path fill='#1D4ED8' d='M12 2 L20 6 V12 C20 17 16.5 21 12 22 C7.5 21 4 17 4 12 V6 Z'/><path fill='#FFFFFF' d='M10.5 12.5 L9 11 L7.8 12.3 L10.5 15 L16 9.3 L14.8 8 Z'/>" },
    fire_station: { color: '#DC2626', svg: "<path fill='#DC2626' d='M12 2 C12 6 8 7 8 12 C8 15.8 10 19 12 22 C14 19 16 15.8 16 12 C16 8 14 7 14 4 C14 6 13 7 12 7 Z'/>" },
    hospital: { color: '#059669', svg: "<path fill='#059669' d='M4 4 H20 V20 H4 Z'/><path fill='#FFFFFF' d='M10 7 H14 V10 H17 V14 H14 V17 H10 V14 H7 V10 H10 Z'/>" },
    courthouse: { color: '#7C3AED', svg: "<path fill='#7C3AED' d='M4 19 H20 V21 H4 Z M4 9 H20 V11 L18 12 H6 L4 11 Z M5 12 H8 V19 H5 Z M11 12 H13 V19 H11 Z M16 12 H19 V19 H16 Z M3 8 L12 3 L21 8 Z'/>" },
    sheriff: { color: '#F59E0B', svg: "<path fill='#F59E0B' d='M12 2 L14.5 7 L20 7.5 L16 11 L17 16 L12 13.5 L7 16 L8 11 L4 7.5 L9.5 7 Z'/>" },
    emergency_services: { color: '#EF4444', svg: "<path fill='#EF4444' d='M12 2 L2 22 H22 Z'/><path fill='#FFFFFF' d='M11 10 H13 V15 H11 Z M11 16 H13 V18 H11 Z'/>" },
    doj: { color: '#0891B2', svg: "<path fill='#0891B2' d='M3 10 H21 V12 H3 Z M5 13 H7 V19 H5 Z M11 13 H13 V19 H11 Z M17 13 H19 V19 H17 Z M12 3 L21 9 H3 Z M3 20 H21 V22 H3 Z'/>" },
    atf: { color: '#2563EB', svg: "<path fill='#2563EB' d='M5 6 H19 V8 L16 10 V18 L12 22 L8 18 V10 L5 8 Z'/>" },
    gov_state: { color: '#6D28D9', svg: "<path fill='#6D28D9' d='M5 9 H19 V20 H5 Z M3 7 L12 2 L21 7 Z'/>" },
    // Jails: rendered as a cell with vertical bars (prison/jail visual)
    jail: { color: '#F59E0B', svg: "<path fill='#F59E0B' d='M4 4 H20 V20 H4 Z'/><path fill='#FFFFFF' d='M8 6 H9 V18 H8 Z M11 6 H12 V18 H11 Z M14 6 H15 V18 H14 Z M17 6 H18 V18 H17 Z'/>" },
  };
  const getPOIMarkerIcon = (type) => {
    const glyph = PLACE_GLYPHS[type] || { color: '#6B7280', svg: "<circle cx='12' cy='12' r='5' fill='#6B7280'/>" };
    return {
      url: makePlaceSvg(glyph.svg, glyph.color),
      scaledSize: window.google?.maps ? new window.google.maps.Size(36, 44) : undefined,
      anchor: window.google?.maps ? new window.google.maps.Point(18, 44) : undefined
    };
  };

  if (loadError) {
    return (
      <div className="flex items-center justify-center h-96 bg-[#131A2A] rounded-lg border border-[#334155]">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 mx-auto text-[#DC2626] mb-4" />
          <p className="text-[#F8FAFC]">Failed to load Google Maps</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="case-address-map-page">
      <MapErrorBanner />
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#F8FAFC] flex items-center gap-3">
            <div className="w-10 h-10 bg-[#3B82F6] rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            Case Address Intelligence
          </h1>
          <p className="text-[#94A3B8] mt-1">Geographic visualization of case-related addresses & public safety locations</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={selectedCase} onValueChange={setSelectedCase}>
            <SelectTrigger className="w-[220px] bg-[#0B0F19] border-[#334155] text-[#F8FAFC]">
              <SelectValue placeholder="Select case" />
            </SelectTrigger>
            <SelectContent className="bg-[#131A2A] border-[#334155] max-h-[300px]">
              <SelectItem value="all" className="text-[#F8FAFC]">All Cases</SelectItem>
              {cases.map(c => (
                <SelectItem key={c.id} value={c.id} className="text-[#F8FAFC]">
                  {c.case_number} - {c.title?.substring(0, 25)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[180px] bg-[#0B0F19] border-[#334155] text-[#F8FAFC]">
              <SelectValue placeholder="Location type" />
            </SelectTrigger>
            <SelectContent className="bg-[#131A2A] border-[#334155]">
              <SelectItem value="all" className="text-[#F8FAFC]">All Types</SelectItem>
              {Object.entries(LOCATION_TYPE_CONFIG).map(([key, config]) => (
                <SelectItem key={key} value={key} className="text-[#F8FAFC]">{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            className="border-[#334155] text-[#94A3B8] hover:bg-[#182136]" 
            onClick={fetchLocations}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Street View Toggle */}
      {showStreetView && streetViewLocation && (
        <Card className="bg-[#131A2A] border-[#334155]">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-[#F8FAFC] text-base flex items-center gap-2">
              <Eye className="w-4 h-4 text-[#3B82F6]" />
              Street View
            </CardTitle>
            <Button 
              variant="outline" 
              size="sm"
              className="border-[#334155] text-[#94A3B8] hover:bg-[#182136]"
              onClick={() => setShowStreetView(false)}
            >
              <Map className="w-4 h-4 mr-2" /> Back to Map
            </Button>
          </CardHeader>
          <CardContent className="p-0 h-[400px]">
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={streetViewLocation}
              zoom={18}
            >
              <StreetViewPanorama
                position={streetViewLocation}
                visible={true}
                options={{
                  enableCloseButton: false,
                  fullscreenControl: true,
                  addressControl: true
                }}
              />
            </GoogleMap>
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      {/* Directions banner */}
      {directionsDestination && (
        <div className="bg-[#0B1E3F] border border-[#1D4ED8]/40 text-[#BFDBFE] rounded-sm px-4 py-3 mb-4 flex items-start justify-between gap-3 flex-wrap" data-testid="directions-banner">
          <div className="flex items-start gap-3 min-w-0">
            <Navigation className="w-5 h-5 text-[#60A5FA] mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white truncate">
                Directions to {directionsDestination.label || directionsDestination.address || 'destination'}
              </p>
              {directionsDestination.address && (
                <p className="text-xs text-[#94A3B8] truncate">{directionsDestination.address}</p>
              )}
              {directionsResult?.routes?.[0]?.legs?.[0] && (
                <p className="text-xs text-[#60A5FA] font-mono mt-0.5">
                  {directionsResult.routes[0].legs[0].distance?.text} • {directionsResult.routes[0].legs[0].duration?.text}
                </p>
              )}
              {directionsError && (
                <p className="text-xs text-amber-300 mt-1">{directionsError}</p>
              )}
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            {directionsDestination.lat != null && directionsDestination.lng != null && (
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${directionsDestination.lat},${directionsDestination.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-mono uppercase tracking-wider border border-[#1D4ED8]/60 text-[#BFDBFE] px-3 py-1.5 rounded-sm hover:bg-[#1D4ED8]/20"
                data-testid="open-google-maps-link"
              >
                Open in Google Maps
              </a>
            )}
            <button
              onClick={() => { setDirectionsDestination(null); setDirectionsResult(null); setDirectionsError(''); }}
              className="text-xs font-mono uppercase tracking-wider text-[#94A3B8] hover:text-white px-2"
              data-testid="clear-directions-btn"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Map */}
        <div className="lg:col-span-3">
          <Card className="bg-[#131A2A] border-[#334155] h-[600px]">
            <CardContent className="p-0 h-full relative">
              {!isLoaded ? (
                <div className="flex items-center justify-center h-full">
                  <RefreshCw className="w-8 h-8 text-[#3B82F6] animate-spin" />
                </div>
              ) : (
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={mapCenter}
                  zoom={11}
                  onLoad={onMapLoad}
                  onIdle={handleMapIdle}
                  options={{
                    styles: darkMapStyle,
                    disableDefaultUI: false,
                    zoomControl: true,
                    mapTypeControl: true,
                    streetViewControl: true,
                    fullscreenControl: true
                  }}
                >
                  {/* Case Location Markers */}
                  {filteredLocations.map(loc => (
                    <Marker
                      key={loc.id}
                      position={{ lat: loc.latitude, lng: loc.longitude }}
                      onClick={() => setSelectedLocation(loc)}
                      icon={getMarkerIcon(loc.location_type)}
                    />
                  ))}

                  {/* Public Safety POI Markers */}
                  {showPublicSafety && publicSafetyPlaces.map(poi => (
                    <Marker
                      key={poi.id}
                      position={{ lat: poi.lat, lng: poi.lng }}
                      onClick={() => setSelectedPOI(poi)}
                      icon={getPOIMarkerIcon(poi.type)}
                    />
                  ))}

                  {/* Case Location Info Window */}
                  {selectedLocation && selectedLocation.latitude && (
                    <InfoWindow
                      position={{ lat: selectedLocation.latitude, lng: selectedLocation.longitude }}
                      onCloseClick={() => setSelectedLocation(null)}
                    >
                      <div className="p-2 min-w-[220px]">
                        <h4 className="font-bold text-gray-900">{selectedLocation.name}</h4>
                        <Badge 
                          className="mt-1 text-xs"
                          style={{ 
                            backgroundColor: `${LOCATION_TYPE_CONFIG[selectedLocation.location_type]?.color}20`,
                            color: LOCATION_TYPE_CONFIG[selectedLocation.location_type]?.color
                          }}
                        >
                          {LOCATION_TYPE_CONFIG[selectedLocation.location_type]?.label || selectedLocation.location_type}
                        </Badge>
                        <p className="text-sm text-gray-600 mt-2">
                          {selectedLocation.address}
                        </p>
                        {selectedLocation.city && (
                          <p className="text-sm text-gray-500">
                            {selectedLocation.city}, {selectedLocation.state} {selectedLocation.zip_code}
                          </p>
                        )}
                        {selectedLocation.case_number && (
                          <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                            <FolderOpen className="w-3 h-3" />
                            Case: {selectedLocation.case_number}
                          </p>
                        )}
                        <button
                          onClick={() => openStreetView(selectedLocation.latitude, selectedLocation.longitude)}
                          className="mt-2 text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                        >
                          <Eye className="w-3 h-3" /> View Street View
                        </button>
                      </div>
                    </InfoWindow>
                  )}

                  {/* POI Info Window */}
                  {selectedPOI && (
                    <InfoWindow
                      position={{ lat: selectedPOI.lat, lng: selectedPOI.lng }}
                      onCloseClick={() => setSelectedPOI(null)}
                    >
                      <div className="p-2 min-w-[200px]">
                        <h4 className="font-bold text-gray-900">{selectedPOI.name}</h4>
                        <Badge 
                          className="mt-1 text-xs"
                          style={{ 
                            backgroundColor: `${PUBLIC_SAFETY_TYPES.find(p => p.type === selectedPOI.type)?.color}20`,
                            color: PUBLIC_SAFETY_TYPES.find(p => p.type === selectedPOI.type)?.color
                          }}
                        >
                          {PUBLIC_SAFETY_TYPES.find(p => p.type === selectedPOI.type)?.label}
                        </Badge>
                        <p className="text-sm text-gray-600 mt-2">{selectedPOI.address}</p>
                        {selectedPOI.rating && (
                          <p className="text-xs text-gray-400 mt-1">Rating: {selectedPOI.rating}/5</p>
                        )}
                        <button
                          onClick={() => openStreetView(selectedPOI.lat, selectedPOI.lng)}
                          className="mt-2 text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                        >
                          <Eye className="w-3 h-3" /> View Street View
                        </button>
                      </div>
                    </InfoWindow>
                  )}

                  {/* Directions destination marker + route */}
                  {directionsDestination && directionsDestination.lat != null && directionsDestination.lng != null && !directionsResult && (
                    <Marker
                      position={{ lat: directionsDestination.lat, lng: directionsDestination.lng }}
                      icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 14,
                        fillColor: '#1D4ED8',
                        fillOpacity: 1,
                        strokeColor: '#FFFFFF',
                        strokeWeight: 3
                      }}
                      title={directionsDestination.label || directionsDestination.address}
                    />
                  )}
                  {directionsResult && (
                    <DirectionsRenderer
                      directions={directionsResult}
                      options={{
                        suppressMarkers: false,
                        polylineOptions: { strokeColor: '#1D4ED8', strokeWeight: 5, strokeOpacity: 0.85 }
                      }}
                    />
                  )}
                </GoogleMap>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Locations Panel */}
        <div className="space-y-4">
          {/* Public Safety Toggle */}
          <Card className="bg-[#131A2A] border-[#334155]">
            <CardContent className="py-3 px-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-[#3B82F6]" />
                  <Label className="text-[#F8FAFC] text-sm">Public Safety Locations</Label>
                </div>
                <Switch 
                  checked={showPublicSafety} 
                  onCheckedChange={setShowPublicSafety}
                  data-testid="public-safety-toggle"
                />
              </div>
              {showPublicSafety && (
                <p className="text-[10px] text-[#94A3B8] mt-2">
                  Showing {publicSafetyPlaces.length} nearby facilities
                </p>
              )}
            </CardContent>
          </Card>

          {/* Case Locations */}
          <Card className="bg-[#131A2A] border-[#334155]">
            <CardHeader className="pb-3">
              <CardTitle className="text-[#F8FAFC] text-base flex items-center gap-2">
                <Layers className="w-4 h-4 text-[#3B82F6]" />
                Case Locations ({filteredLocations.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 max-h-[280px] overflow-y-auto">
              {filteredLocations.length === 0 ? (
                <p className="text-[#94A3B8] text-sm text-center py-4">No locations found</p>
              ) : (
                filteredLocations.map(loc => {
                  const config = LOCATION_TYPE_CONFIG[loc.location_type] || LOCATION_TYPE_CONFIG.OTHER;
                  const IconComponent = config.icon;
                  return (
                    <div
                      key={loc.id}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedLocation?.id === loc.id 
                          ? 'bg-[#1D4ED8]/20 border-[#1D4ED8]' 
                          : 'bg-[#0B0F19] border-[#334155] hover:border-[#475569]'
                      }`}
                      onClick={() => centerOnLocation(loc)}
                      data-testid={`location-card-${loc.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div 
                          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${config.color}20` }}
                        >
                          <IconComponent className="w-4 h-4" style={{ color: config.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[#F8FAFC] font-medium text-sm truncate">{loc.name}</p>
                          <p className="text-xs text-[#94A3B8] truncate">{loc.address}</p>
                          {loc.case_number && (
                            <p className="text-[10px] text-[#475569] mt-1">Case: {loc.case_number}</p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-1 h-auto text-[#94A3B8] hover:text-[#3B82F6]"
                          onClick={(e) => {
                            e.stopPropagation();
                            openStreetView(loc.latitude, loc.longitude);
                          }}
                          title="Street View"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>

          {/* Legend */}
          <Card className="bg-[#131A2A] border-[#334155]">
            <CardHeader className="pb-2">
              <CardTitle className="text-[#F8FAFC] text-sm">Case Locations Legend</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-2">
              {Object.entries(LOCATION_TYPE_CONFIG).map(([key, config]) => {
                const IconComponent = config.icon;
                const count = locations.filter(l => l.location_type === key).length;
                return (
                  <div 
                    key={key}
                    className="flex items-center gap-2 p-1.5 text-xs"
                  >
                    <div 
                      className="w-4 h-4 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: config.color }}
                    >
                      <IconComponent className="w-2.5 h-2.5 text-white" />
                    </div>
                    <span className="text-[#94A3B8]">{config.label}</span>
                    <span className="text-[#475569] ml-auto">({count})</span>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Public Safety Legend */}
          {showPublicSafety && (
            <Card className="bg-[#131A2A] border-[#334155]">
              <CardHeader className="pb-2">
                <CardTitle className="text-[#F8FAFC] text-sm">Public Safety</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-1.5">
                {PUBLIC_SAFETY_TYPES.map(({ type, label, icon: Icon, color }) => {
                  const count = publicSafetyPlaces.filter(p => p.type === type).length;
                  return (
                    <div 
                      key={type}
                      className="flex items-center gap-2 text-xs"
                    >
                      <div 
                        className="w-4 h-4 rounded flex items-center justify-center"
                        style={{ backgroundColor: `${color}30` }}
                      >
                        <Icon className="w-2.5 h-2.5" style={{ color }} />
                      </div>
                      <span className="text-[#94A3B8] flex-1">{label}</span>
                      <span className="text-[#475569]">({count})</span>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default CaseAddressMap;
